'use client';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { ProductsContainerView } from './product-container-view';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

interface ProductsContainerProps {
  products: Product[];
  /** Homepage deferred loader already owns guest/customer fetching and cache identity. */
  managedExternally?: boolean;
  locale?: string;
  query?: NonNullable<Parameters<typeof fetchProducts>[0]['query']>;
  personalizationKey?: string;
  title: string;
  description?: string;
  variant: 'carousel' | 'grid';
  className?: { section?: string };
}

export function ProductsContainer(props: ProductsContainerProps) {
  // Avoid a redundant disabled observer when homepage/related products owns data.
  return props.managedExternally ? (
    <ProductsContainerView {...props} />
  ) : (
    <QueriedProductsContainer {...props} />
  );
}

function QueriedProductsContainer({
  products: initialProducts,
  managedExternally = false,
  locale,
  query,
  personalizationKey,
  title,
  description,
  variant,
  className,
}: ProductsContainerProps) {
  const { me, isInitialized } = useCustomerStore();
  const productRequestUri = query ? buildProductsRequest(query).uri : null;

  const { data: queryProducts = initialProducts } = useQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      personalizationKey ?? 'static-product-container',
      locale ?? 'static',
      productRequestUri,
      me?.customer.id ?? 'guest',
    ],
    queryFn: ({ signal }) => {
      if (!locale || !query) return initialProducts;

      return fetchProducts({ locale, query, authToken: me?.authToken, signal }).then(
        response => response.data,
      );
    },
    enabled: !managedExternally && isInitialized && !!locale && !!query,
    initialData:
      !managedExternally && (!locale || !query || !me?.authToken) ? initialProducts : undefined,
    placeholderData: managedExternally ? undefined : initialProducts,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const products = managedExternally ? initialProducts : queryProducts;

  return (
    <ProductsContainerView
      products={products}
      title={title}
      description={description}
      variant={variant}
      className={className}
    />
  );
}
